import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";
import { moduleUsageInfoDtoSchema } from "../../../shared/capacity/overCapacity";

const nonEmpty = z.string().min(1);
const price = z.number().nonnegative();
// Display-only strings the UI just renders — tolerate empty / missing / null so
// one sparse-but-valid row can't fail the whole response parse.
const displayText = z
  .string()
  .nullish()
  .transform((value) => value ?? "");

export const subscriptionModuleStatusDtoSchema = z.enum([
  "ACTIVE",
  "INACTIVE",
  "EXPIRED",
  "SCHEDULED",
]);
export const deviceAllocationDurationDtoSchema = z.literal("YEARLY");

export const subscriptionPartyDtoSchema = z.object({
  id: nonEmpty,
  nameEn: nonEmpty,
  // English-only subscribers legitimately have no Arabic name — don't 500 the
  // screen over it.
  nameAr: displayText,
});

// Lenient by design: the create/list nested `module` carries `price` and
// timestamps the detail response's does not, and zod strips unknown keys, so
// one schema parses both without over-committing to either shape.
export const subscriptionModuleRefDtoSchema = z.object({
  id: nonEmpty,
  slug: nonEmpty,
  nameEn: nonEmpty,
  nameAr: nonEmpty,
  descriptionEn: displayText,
  descriptionAr: displayText,
});

export const subscriptionDeviceTypeDtoSchema = z.object({
  id: nonEmpty,
  nameEn: nonEmpty,
  nameAr: nonEmpty,
});

export const subscriptionDeviceModelDtoSchema = z.object({
  id: nonEmpty,
  deviceTypeId: nonEmpty,
  nameEn: nonEmpty,
  nameAr: nonEmpty,
});

export const subscriptionDeviceAllocationDtoSchema = z.object({
  id: nonEmpty,
  deviceTypeId: nonEmpty,
  deviceTypeNameEn: nonEmpty,
  deviceTypeNameAr: nonEmpty,
  deviceModelId: nonEmpty,
  deviceModelNameEn: nonEmpty,
  deviceModelNameAr: nonEmpty,
  quantity: z.number().int().positive(),
  duration: deviceAllocationDurationDtoSchema,
});

/** The shape a create/addModules/list-row line carries. */
export const subscriptionModuleLineDtoSchema = z.object({
  id: nonEmpty,
  moduleId: nonEmpty,
  module: subscriptionModuleRefDtoSchema,
  purchasingPrice: price,
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  status: subscriptionModuleStatusDtoSchema,
  // When the line was sold; read for the list's creation date.
  createdAt: z.string().optional(),
});

/** Only `SubscriptionDetails` lines (current/history/future/assignableModules) carry these two. */
export const subscriptionDetailModuleLineDtoSchema =
  subscriptionModuleLineDtoSchema.extend({
    // Kept loose (no int/sign constraint) — they're display buckets, and an
    // out-of-range value must not sink the whole details response.
    periodDays: z.number().nullable(),
    remainingDays: z.number().nullable(),
  });

/**
 * `assignableModules` on the details response comes back in the same *line*
 * shape as `current`/`history`/`future` (see docs/api/nasam-api.md), but as a
 * not-yet-persisted line: the top-level `id` is an empty string and only the
 * nested `module` reference matters to the "Add module" picker. Older / mock
 * payloads send the bare reference at the top level instead. Accept either and
 * reduce to the reference — validating nothing but `module` on the line form.
 */
export const assignableModuleDtoSchema = z.union([
  z
    .object({ module: subscriptionModuleRefDtoSchema })
    .transform((line) => line.module),
  subscriptionModuleRefDtoSchema,
]);

export const subscriptionDtoSchema = z.object({
  id: nonEmpty,
  subscriberId: nonEmpty,
  subscriber: subscriptionPartyDtoSchema,
  modules: z.array(subscriptionModuleLineDtoSchema).min(1),
  // Pending-contract (see docs/api/gaps.md) -- no device field exists on the
  // real response; kept lenient so MSW can still exercise it.
  devices: z.array(subscriptionDeviceAllocationDtoSchema).default([]),
  // Present on list rows; absent on the create/addModules response -- the
  // mapper derives both the same way the backend documents it deriving them.
  totalSubscriptionPrice: price.optional(),
  status: subscriptionModuleStatusDtoSchema.optional(),
  createdAt: nonEmpty,
  updatedAt: nonEmpty,
});

export const subscriptionDetailsDtoSchema = z.object({
  id: nonEmpty,
  subscriber: subscriptionPartyDtoSchema,
  status: subscriptionModuleStatusDtoSchema,
  totalSubscriptionPrice: price,
  current: z.array(subscriptionDetailModuleLineDtoSchema),
  history: z.array(subscriptionDetailModuleLineDtoSchema),
  future: z.array(subscriptionDetailModuleLineDtoSchema),
  assignableModules: z.array(assignableModuleDtoSchema),
  devices: z.array(subscriptionDeviceAllocationDtoSchema).default([]),
});

export const subscriptionListResponseDtoSchema = z.object({
  data: z.array(subscriptionDtoSchema),
  meta: paginationMetaDtoSchema,
});

/** A sale's response: the subscription, and in `usageInfo` where every
 *  module the seller holds now stands. */
export const subscriptionSaleResponseDtoSchema = z.object({
  data: subscriptionDtoSchema.extend({ usageInfo: moduleUsageInfoDtoSchema }),
  message: z.string().optional(),
});

export const subscriptionResponseDtoSchema = z.object({
  data: subscriptionDtoSchema,
});

export const subscriptionDetailsResponseDtoSchema = z.object({
  data: subscriptionDetailsDtoSchema,
});

export const subscriptionDeviceTypesResponseDtoSchema = z.object({
  data: z.array(subscriptionDeviceTypeDtoSchema),
});

export const subscriptionDeviceModelsResponseDtoSchema = z.object({
  data: z.array(subscriptionDeviceModelDtoSchema),
});

export const subscriptionModuleWriteDtoSchema = z.object({
  moduleId: nonEmpty,
  purchasingPrice: price.optional(),
  startDate: nonEmpty.nullable(),
  endDate: nonEmpty.nullable(),
});

export const subscriptionDeviceAllocationWriteDtoSchema = z.object({
  deviceTypeId: nonEmpty,
  deviceModelId: nonEmpty,
  quantity: z.number().int().positive(),
  duration: deviceAllocationDurationDtoSchema,
});

export const subscriptionWriteDtoSchema = z.object({
  subscriberId: nonEmpty,
  modules: z.array(subscriptionModuleWriteDtoSchema).min(1),
});

export const subscriptionAddModulesWriteDtoSchema = z.object({
  modules: z.array(subscriptionModuleWriteDtoSchema).min(1),
});

export type DeviceAllocationDurationDto = z.infer<
  typeof deviceAllocationDurationDtoSchema
>;
export type SubscriptionDeviceTypeDto = z.infer<
  typeof subscriptionDeviceTypeDtoSchema
>;
export type SubscriptionDeviceModelDto = z.infer<
  typeof subscriptionDeviceModelDtoSchema
>;
export type SubscriptionDeviceAllocationDto = z.infer<
  typeof subscriptionDeviceAllocationDtoSchema
>;
export type SubscriptionDeviceAllocationWriteDto = z.infer<
  typeof subscriptionDeviceAllocationWriteDtoSchema
>;
export type SubscriptionDeviceTypesResponseDto = z.infer<
  typeof subscriptionDeviceTypesResponseDtoSchema
>;
export type SubscriptionDeviceModelsResponseDto = z.infer<
  typeof subscriptionDeviceModelsResponseDtoSchema
>;
export type SubscriptionModuleRefDto = z.infer<
  typeof subscriptionModuleRefDtoSchema
>;
export type SubscriptionModuleLineDto = z.infer<
  typeof subscriptionModuleLineDtoSchema
>;
export type SubscriptionDetailModuleLineDto = z.infer<
  typeof subscriptionDetailModuleLineDtoSchema
>;
export type SubscriptionDto = z.infer<typeof subscriptionDtoSchema>;
export type SubscriptionDetailsDto = z.infer<
  typeof subscriptionDetailsDtoSchema
>;
export type SubscriptionWriteDto = z.infer<typeof subscriptionWriteDtoSchema>;
export type SubscriptionModuleWriteDto = z.infer<
  typeof subscriptionModuleWriteDtoSchema
>;
